import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { apiService } from '../services/apiService';
import { showToast } from '../components/Toast';
import TaskSubNav from '../components/TaskSubNav';
import { DSPageHeader, DSButton, DSInput, DSSelect, DSIcon, DSBadge } from '../design-system/primitives';
import StatusBadge from '../components/StatusBadge';
import './taskManagementShared.css';
import './TaskTemplateLibrary.css';

const BLANK_CATEGORY_FORM = { id: null, name: '', sortOrder: 0, isActive: true, defaultAssigneeUserId: '' };

function TaskTemplateLibrary() {
  const navigate = useNavigate();
  const [templates, setTemplates] = useState([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [expanded, setExpanded] = useState({});
  const [openMenuId, setOpenMenuId] = useState(null);
  const [showCategoryManager, setShowCategoryManager] = useState(false);
  const [categories, setCategories] = useState([]);
  const [assignableUsers, setAssignableUsers] = useState([]);
  const [categoryForm, setCategoryForm] = useState(BLANK_CATEGORY_FORM);
  const [categorySubmitting, setCategorySubmitting] = useState(false);
  const [categoryError, setCategoryError] = useState('');

  useEffect(() => {
    const closeMenu = () => setOpenMenuId(null);
    document.addEventListener('click', closeMenu);
    return () => document.removeEventListener('click', closeMenu);
  }, []);

  const loadTemplates = async () => {
    try {
      setLoading(true);
      const response = await apiService.getTaskTemplates(search ? { search } : {});
      setTemplates(response.data || []);
    } catch (err) {
      setError(err.response?.data?.error || 'Unable to load templates');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const timeout = setTimeout(loadTemplates, 250);
    return () => clearTimeout(timeout);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  // Refetch whenever this tab/page regains focus (e.g. returning from Task
  // Detail after completing the open instance elsewhere), as a safety net
  // beyond the on-mount fetch above.
  useEffect(() => {
    const handleFocus = () => loadTemplates();
    window.addEventListener('focus', handleFocus);
    document.addEventListener('visibilitychange', handleFocus);
    return () => {
      window.removeEventListener('focus', handleFocus);
      document.removeEventListener('visibilitychange', handleFocus);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const grouped = templates.reduce((acc, t) => {
    const key = t.category_name || 'Uncategorized';
    acc[key] = acc[key] || [];
    acc[key].push(t);
    return acc;
  }, {});

  const toggleCategory = (name) => {
    setExpanded((prev) => ({ ...prev, [name]: !prev[name] }));
  };

  const handleClone = async (templateId) => {
    try {
      await apiService.cloneTaskTemplate(templateId);
      await loadTemplates();
    } catch (err) {
      showToast(err.response?.data?.error || 'Failed to clone template', 'error');
    }
  };

  const handleDeactivate = async (templateId, isActive) => {
    try {
      if (isActive) {
        await apiService.deactivateTaskTemplate(templateId);
      } else {
        await apiService.reactivateTaskTemplate(templateId);
      }
      await loadTemplates();
    } catch (err) {
      showToast(err.response?.data?.error || 'Failed to update template', 'error');
    }
  };

  const handleSpawn = async (templateId) => {
    try {
      await apiService.spawnTaskFromTemplate(templateId);
      showToast('Task spawned successfully', 'success');
      await loadTemplates();
    } catch (err) {
      showToast(err.response?.data?.error || 'Failed to spawn task', 'error');
    }
  };

  const loadCategories = async () => {
    try {
      const response = await apiService.getTaskCategories({ includeInactive: true });
      setCategories(response.data || []);
    } catch (err) {
      setCategoryError(err.response?.data?.error || 'Unable to load categories');
    }
  };

  const openCategoryManager = () => {
    setShowCategoryManager(true);
    setCategoryForm(BLANK_CATEGORY_FORM);
    setCategoryError('');
    loadCategories();
    if (assignableUsers.length === 0) {
      apiService.getTaskAssignableUsers().then((res) => setAssignableUsers(res.data || [])).catch(() => {});
    }
  };

  const startEditCategory = (category) => {
    setCategoryForm({
      id: category.id,
      name: category.name,
      sortOrder: category.sort_order,
      isActive: category.is_active,
      defaultAssigneeUserId: category.default_assignee_user_id || ''
    });
    setCategoryError('');
  };

  const handleSaveCategory = async () => {
    if (!categoryForm.name.trim()) {
      setCategoryError('Category name is required');
      return;
    }
    try {
      setCategorySubmitting(true);
      setCategoryError('');
      const payload = {
        name: categoryForm.name,
        sortOrder: Number(categoryForm.sortOrder) || 0,
        defaultAssigneeUserId: categoryForm.defaultAssigneeUserId || null
      };
      if (categoryForm.id) {
        payload.isActive = categoryForm.isActive;
        await apiService.updateTaskCategory(categoryForm.id, payload);
      } else {
        await apiService.createTaskCategory(payload);
      }
      setCategoryForm(BLANK_CATEGORY_FORM);
      await loadCategories();
      await loadTemplates();
    } catch (err) {
      setCategoryError(err.response?.data?.error || 'Failed to save category');
    } finally {
      setCategorySubmitting(false);
    }
  };

  return (
    <div className="tm-page">
      <DSPageHeader
        title="Template Library"
        actions={
          <div className="tm-actions">
            <DSButton appearance="outline" tone="secondary" onClick={openCategoryManager}>Manage Categories</DSButton>
            <DSButton appearance="solid" tone="primary" onClick={() => navigate('/tasks/templates/new')}>+ New</DSButton>
          </div>
        }
      />

      <DSInput
        type="search"
        placeholder="Search templates..."
        value={search}
        onChange={(e) => setSearch(e.target.value)}
      />

      {error && <div className="error-message">{error}</div>}

      {loading ? (
        <div className="loading-inline">Loading templates...</div>
      ) : Object.keys(grouped).length === 0 ? (
        <div className="tm-empty-state"><h2>No templates yet</h2></div>
      ) : (
        Object.entries(grouped).map(([categoryName, items]) => (
          <div key={categoryName} className="tm-section">
            <div className="tm-category-header" onClick={() => toggleCategory(categoryName)}>
              <h2>{categoryName} ({items.length})</h2>
              <span>{expanded[categoryName] === false ? <DSIcon name="chevronRight" /> : <DSIcon name="chevronDown" />}</span>
            </div>
            {expanded[categoryName] !== false && (
              <div className="tm-template-list">
                {items.map((template) => (
                  <div key={template.id} className="tm-template-row">
                    <div>
                      <strong>{template.title}</strong>
                      {!template.is_active && <StatusBadge variant="neutral" size="sm" uppercase>INACTIVE</StatusBadge>}
                      {template.open_task_id && <StatusBadge variant="success" size="sm" uppercase>LIVE</StatusBadge>}
                      <div className="tm-task-tags">
                        <StatusBadge variant="neutral" size="sm">
                          {template.template_type === 'auto_recurring' ? <><DSIcon name="refresh" /> Auto-recurring</> : <><DSIcon name="clipboard" /> On-demand</>}
                        </StatusBadge>
                        <DSBadge variant="brand" size="sm" leadingIcon={<DSIcon name="star" />}>{template.point_value}pt</DSBadge>
                        {template.default_bounty_amount && (
                          <StatusBadge variant="warning" size="sm">${template.default_bounty_amount}</StatusBadge>
                        )}
                      </div>
                    </div>
                    <div className="tm-actions">
                      {template.open_task_id ? (
                        <DSButton to={`/tasks/${template.open_task_id}`} tone="secondary" appearance="outline" size="sm">
                          View Open Task
                        </DSButton>
                      ) : (
                        <DSButton appearance="outline" tone="secondary" size="sm" onClick={() => handleSpawn(template.id)}>Spawn Now</DSButton>
                      )}
                      <div className="tm-dropdown" onClick={(e) => e.stopPropagation()}>
                        <DSButton
                          appearance="outline"
                          tone="secondary"
                          size="sm"
                          className="tm-dropdown-toggle"
                          onClick={() => setOpenMenuId(openMenuId === template.id ? null : template.id)}
                          aria-label="More actions"
                        >
                          ⋯
                        </DSButton>
                        {openMenuId === template.id && (
                          <div className="tm-dropdown-menu">
                            <Link to={`/tasks/templates/${template.id}/edit`} className="tm-dropdown-item"><DSIcon name="edit" /> Edit</Link>
                            <button
                              type="button"
                              className="tm-dropdown-item"
                              onClick={() => { setOpenMenuId(null); handleClone(template.id); }}
                            >
                              <DSIcon name="clipboard" /> Clone
                            </button>
                            <button
                              type="button"
                              className={`tm-dropdown-item ${template.is_active ? 'tm-dropdown-item-danger' : ''}`}
                              onClick={() => { setOpenMenuId(null); handleDeactivate(template.id, template.is_active); }}
                            >
                              {template.is_active ? <><DSIcon name="trash" /> Delete</> : <><DSIcon name="recycle" /> Restore</>}
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        ))
      )}

      {showCategoryManager && (
        <div className="modal-overlay" onClick={(e) => e.target === e.currentTarget && setShowCategoryManager(false)}>
          <div className="modal">
            <h2>Manage Categories</h2>
            {categoryError && <div className="error-message">{categoryError}</div>}

            <div className="tm-template-list">
              {categories.map((c) => (
                <div key={c.id} className="tm-template-row">
                  <div>
                    <strong>{c.name}</strong>
                    {!c.is_active && <StatusBadge variant="neutral" size="sm" uppercase>INACTIVE</StatusBadge>}
                    <div className="tm-suggestion-meta">
                      {c.default_assignee_name ? `Auto-assigns to ${c.default_assignee_name}` : 'Open pool (no default assignee)'}
                    </div>
                  </div>
                  <DSButton appearance="outline" tone="secondary" size="sm" onClick={() => startEditCategory(c)}>Edit</DSButton>
                </div>
              ))}
            </div>

            <div className="tm-section">
              <h3>{categoryForm.id ? `Edit "${categoryForm.name}"` : 'New Category'}</h3>
              <div className="tm-form-group">
                <DSInput
                  label="Name"
                  type="text"
                  value={categoryForm.name}
                  onChange={(e) => setCategoryForm((prev) => ({ ...prev, name: e.target.value }))}
                />
              </div>
              <div className="tm-form-row">
                <div className="tm-form-group">
                  <DSInput
                    label="Sort Order"
                    type="number"
                    value={categoryForm.sortOrder}
                    onChange={(e) => setCategoryForm((prev) => ({ ...prev, sortOrder: e.target.value }))}
                  />
                </div>
                {categoryForm.id && (
                  <div className="tm-form-group">
                    <label>
                      <input
                        type="checkbox"
                        checked={categoryForm.isActive}
                        onChange={(e) => setCategoryForm((prev) => ({ ...prev, isActive: e.target.checked }))}
                      />
                      {' '}Active
                    </label>
                  </div>
                )}
              </div>
              <div className="tm-form-group">
                <DSSelect
                  label="Default Assignee"
                  value={categoryForm.defaultAssigneeUserId}
                  onChange={(e) => setCategoryForm((prev) => ({ ...prev, defaultAssigneeUserId: e.target.value }))}
                >
                  <option value="">None (open pool)</option>
                  {assignableUsers.map((u) => (
                    <option key={u.id} value={u.id}>{u.first_name} {u.last_name}</option>
                  ))}
                </DSSelect>
                <div className="tm-suggestion-meta">
                  Tasks created in this category with no explicit assignee auto-assign here instead of sitting open.
                </div>
              </div>
            </div>

            <div className="modal-actions">
              <DSButton appearance="solid" tone="primary" onClick={handleSaveCategory} disabled={categorySubmitting}>
                {categorySubmitting ? 'Saving...' : (categoryForm.id ? 'Save Changes' : 'Create Category')}
              </DSButton>
              {categoryForm.id && (
                <DSButton appearance="outline" tone="neutral" onClick={() => setCategoryForm(BLANK_CATEGORY_FORM)} disabled={categorySubmitting}>
                  Cancel Edit
                </DSButton>
              )}
              <DSButton appearance="outline" tone="neutral" onClick={() => setShowCategoryManager(false)} disabled={categorySubmitting}>
                Close
              </DSButton>
            </div>
          </div>
        </div>
      )}

      <TaskSubNav />
    </div>
  );
}

export default TaskTemplateLibrary;
